import GeoJSON from "ol/format/GeoJSON";
import { AttributeValue } from "./AttributeValue";
import { ISearchDateAttributeConvert } from "./ISearchDateAttributeConvert";
import {
  TraVosLayerType,
  TraVosRoadType,
} from "@/geoMap/layerFactory/TraVosLayerName";
import { FeatureType } from "@/geoMap/define/FeatureType";
import { getLayerKey } from "@/geoMap/LayerName";
import { WFSGetFeatureManager } from "@/geoMap/net/WFSGetFeatureManager";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import { AttributeView } from "./AttributeView";
import { TraVosCodeUtil } from "./util/TraVosCodeUtil";
import { ExportValue } from "./ExportValue";
import { parseTimeCode } from "./util/TimeCodeUtil";
import { Geometry } from "ol/geom";

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

/**
 * 対象FeatureType
 */
const FEATURE_TYPE = FeatureType.TraVosCalcHourSearchDateAttribute;

/**
 * 出力JSONファイル名
 */
const VALUE_JSON_FILE_NAME = "traf_q-8-2_yyyyMMdd_HHmmss.geojson";

/**
 * 出力CSVファイル名
 */
const VALUE_CSV_FILE_NAME = "traf_q-8-2_yyyyMMdd_HHmmss.csv";

/**
 * 属性データ
 */
const PROPERTY_MAP = [
  { key: "常時観測点コード", isSimple: true, isCommon: true },
  { key: "収集時間フラグ（5分間／1時間）", isSimple: true, isCommon: true },
  { key: "観測年月日", isSimple: true, isCommon: false },
  { key: "時間帯", isSimple: true, isCommon: false },
  { key: "上り・小型交通量", isSimple: true, isCommon: false },
  { key: "上り・大型交通量", isSimple: true, isCommon: false },
  { key: "上り・車種判別不能交通量", isSimple: true, isCommon: false },
  { key: "下り・小型交通量", isSimple: true, isCommon: false },
  { key: "下り・大型交通量", isSimple: true, isCommon: false },
  { key: "下り・車種判別不能交通量", isSimple: true, isCommon: false },
  { key: "地方整備局等番号", isSimple: false, isCommon: false },
  { key: "開発建設部／都道府県コード", isSimple: false, isCommon: false },
  { key: "上り・停電", isSimple: false, isCommon: false },
  { key: "上り・ループ異常", isSimple: false, isCommon: false },
  { key: "上り・超音波異常", isSimple: false, isCommon: false },
  { key: "上り・欠測", isSimple: false, isCommon: false },
  { key: "下り・停電", isSimple: false, isCommon: false },
  { key: "下り・ループ異常", isSimple: false, isCommon: false },
  { key: "下り・超音波異常", isSimple: false, isCommon: false },
  { key: "下り・欠測", isSimple: false, isCommon: false },
];

/**
 * 画面フォーマット用交通量判別定数
 */
const TRAFFIC_VOLUME = "交通量";

/**
 * 画面幅
 */
const VALUE_WIDTH = 526;

/**
 * 画面高さ
 */
const VALUE_HEIGHT = 335;

/**
 * 画面最大高さ
 */
const VALUE_MAX_HEIGHT = 420;

/**
 * 画面タイトル
 */
const VALUE_TITLE = "交通量データ（過去）属性表示画面(常設トラカン計測値1時間)";

/**
 * 過去データ
 * TraVos属性表示画面（計測値１時間）用過去属性取得クラス
 */
export class TraVosCalcHourSearchDateAttributeConvert
  implements ISearchDateAttributeConvert
{
  /**
   * GeoJSON
   */
  private _geoJSON: any;

  /**
   * TraVosレイヤ名型定義
   */
  private _layerType: TraVosLayerType;

  /**
   * TraVos道路種別コード
   */
  private _roadType: TraVosRoadType;
  get roadType() {
    return this._roadType;
  }

  /**
   * 常時観測点コード
   */
  private _regularObservationCd: string;
  /**
   * 収集時間フラグ
   */
  private _timeCorrectionFlag: string;
  /**
   * TraVos道路種別コード
   */
  private _timeCode: string;
  /**
   * 地物通称
   */
  private _commonName: string;

  /**
   * コンストラクタ
   * @param geoJSON GeoJSON
   * @param layerType TraVosレイヤタイプ
   * @param roadTypes TraVos道路種別
   * @param regularObservationCd 常時観測点
   * @param commonName 通称
   * @param timeCorrectionFlag 収集時間フラグ
   * @param timeCode 時間コード
   */
  constructor(
    geoJSON: any,
    layerType: TraVosLayerType,
    roadType: TraVosRoadType,
    regularObservationCd: string,
    commonName: string,
    timeCorrectionFlag: string,
    timeCode: string
  ) {
    this._geoJSON = geoJSON;
    this._layerType = layerType;
    this._roadType = roadType;
    this._regularObservationCd = regularObservationCd;
    this._commonName = commonName;
    this._timeCorrectionFlag = timeCorrectionFlag;
    this._timeCode = timeCode;
  }

  /**
   * 時間コード取得
   */
  getTimeCode(): string {
    return this._timeCode;
  }

  /**
   * 地物タイプ取得
   */
  getFeatureType(): FeatureType {
    return FEATURE_TYPE;
  }

  /**
   * 属性データ更新
   * @param timeCode 時間コード
   */
  // 地物情報をtimeCodeで取得なおして、結果やtimeCodeを保持しておく
  async load(timeCode: string): Promise<ISearchDateAttributeConvert> {
    // 1時間属性：分単位は切り捨て
    const formattedTimeCode = timeCode.slice(0, 10) + "00";
    const layerKey = getLayerKey(this._layerType).replace("v_", "t_");
    const cqlQuery = `"道路種別"='${this._roadType}' AND "時間コード" = ${formattedTimeCode} AND "常時観測点コード" = ${this._regularObservationCd}`;
    const cqlParameter = new CqlParameter(layerKey, cqlQuery);
    const WFSGetter = new WFSGetFeatureManager();
    const loadedGeoJSON = await WFSGetter.getInfo(URL_API, [
      cqlParameter,
    ]).catch((error) => {
      throw error;
    });
    return new TraVosCalcHourSearchDateAttributeConvert(
      loadedGeoJSON,
      this._layerType,
      this._roadType,
      this._regularObservationCd,
      this._commonName,
      this._timeCorrectionFlag,
      formattedTimeCode
    );
  }

  /**
   * 画面用フォーマット取得
   */
  getViewFormatData(): AttributeView {
    const attributeValues: AttributeValue[] = [];
    const features = new GeoJSON().readFeatures(this._geoJSON);
    const { year, month, day, hour, minute } = parseTimeCode(this._timeCode);
    if (features.length) {
      const feature = features[0];
      // 画面項目に基づき値を取得（値がない場合は"-"）
      PROPERTY_MAP.forEach((property) => {
        if (property.key === PROPERTY_MAP[0].key) {
          /**
           * 常時観測点コード(クラス変数)
           */
          attributeValues.push({
            key: property.key,
            value: `${this._regularObservationCd}（${this._commonName}）`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[1].key) {
          /**
           * 収集時間フラグを画面用文字列に変換する（クラス変数）
           */
          attributeValues.push({
            key: property.key,
            value: TraVosCodeUtil.toCollectTimeCodeViewString(
              this._timeCorrectionFlag
            ),
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[2].key) {
          /**
           * 観測年月日
           */
          attributeValues.push({
            key: property.key,
            value: `${year}/${month}/${day}`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[3].key) {
          /**
           * 時間帯
           */
          attributeValues.push({
            key: property.key,
            value: `${hour}:${minute}`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[10].key) {
          /**
           * 地方整備局等番号を画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toRegionalBureauViewString(
                feature.get(property.key)
              ) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[11].key) {
          /**
           * 開発建設部／都道府県コードを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toPrefectureCodeViewString(
                feature.get(property.key)
              ) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (
          property.key === PROPERTY_MAP[12].key ||
          property.key === PROPERTY_MAP[16].key
        ) {
          /**
           * 停電フラグを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toTraVosBreakoutCodeViewString(
                feature.get(property.key)
              ) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (
          property.key === PROPERTY_MAP[13].key ||
          property.key === PROPERTY_MAP[17].key
        ) {
          /**
           * ループ異常フラグを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toLoopCodeViewString(feature.get(property.key)) ??
              "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (
          property.key === PROPERTY_MAP[14].key ||
          property.key === PROPERTY_MAP[18].key
        ) {
          /**
           * 超音波異常フラグを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value:
              TraVosCodeUtil.toUltrasonicCodeViewString(
                feature.get(property.key)
              ) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (
          property.key === PROPERTY_MAP[15].key ||
          property.key === PROPERTY_MAP[19].key
        ) {
          /**
           * 欠測フラグを画面用文字列に変換する
           */
          attributeValues.push({
            key: property.key,
            value: TraVosCodeUtil.toMissingCodeViewString(
              feature.get(property.key)
            ),
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key.indexOf(TRAFFIC_VOLUME) !== -1) {
          /**
           * 交通量データ系のラベルに単位を追加する
           */
          attributeValues.push({
            key: property.key + "（台／時間）",
            value: feature.get(property.key) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else {
          attributeValues.push({
            key: property.key,
            value: feature.get(property.key) ?? "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        }
      });
    } else {
      // クラス変数を参照する項目以外は"-"を挿入
      PROPERTY_MAP.forEach((property) => {
        if (property.key === PROPERTY_MAP[0].key) {
          /**
           * 常時観測点コード(クラス変数)
           */
          attributeValues.push({
            key: property.key,
            value: `${this._regularObservationCd}（${this._commonName}）`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[1].key) {
          /**
           * 収集時間フラグを画面用文字列に変換する（クラス変数）
           */
          attributeValues.push({
            key: property.key,
            value: TraVosCodeUtil.toCollectTimeCodeViewString(
              this._timeCorrectionFlag
            ),
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[2].key) {
          /**
           * 観測年月日
           */
          attributeValues.push({
            key: property.key,
            value: `${year}/${month}/${day}`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key === PROPERTY_MAP[3].key) {
          /**
           * 時間帯
           */
          attributeValues.push({
            key: property.key,
            value: `${hour}:${minute}`,
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else if (property.key.indexOf(TRAFFIC_VOLUME) !== -1) {
          /**
           * 交通量データ系のラベルに単位を追加する
           */
          attributeValues.push({
            key: property.key + "（台／時間）",
            value: "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        } else {
          attributeValues.push({
            key: property.key,
            value: "-",
            isSimple: property.isSimple,
            isCommon: property.isCommon,
          });
        }
      });
    }

    const attributeView: AttributeView = {
      width: VALUE_WIDTH,
      height: VALUE_HEIGHT,
      maxheight: VALUE_MAX_HEIGHT,
      title: VALUE_TITLE,
      attributeValues: attributeValues,
    };

    return attributeView;
  }

  /**
   * ファイル出力用項目取得
   */
  getExportValue(): ExportValue {
    const feature = new GeoJSON().readFeatures(this._geoJSON)[0];
    feature.getGeometry().transform("EPSG:3857", "EPSG:4326");
    const attributeValues = this.getViewFormatData().attributeValues;
    return new ExportValue(
      feature,
      attributeValues,
      VALUE_JSON_FILE_NAME,
      VALUE_CSV_FILE_NAME
    );
  }

  /**
   * ジオメトリを取得
   */
  getGeometry(): Geometry {
    const feature = new GeoJSON().readFeatures(this._geoJSON)[0];
    return feature?.getGeometry().transform("EPSG:3857", "EPSG:4326");
  }
}
